import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  CalendarDays,
  Columns2,
  Compass,
  Crosshair,
  Download,
  Maximize2,
  Minimize2,
  Minus,
  PlayCircle,
  Plus,
  Sun,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { useModule } from "../../contexts/ModuleContext";
import { resolveProjectMedia } from "../../data/siteVisualsMedia";
import { fetchSurveyImages } from "../../services/projectSurveyService";
import ConstructionLayout from "../construction/ConstructionLayout";
import TiledOrthomosaicViewer, {
  fitTiledViewer,
  rotateTiledViewerHome,
  zoomTiledViewer,
} from "./TiledOrthomosaicViewer";
import WalkthroughPlayer from "./WalkthroughPlayer";
import ViewerLoading from "./ViewerLoading";
import { Feature, isFeatureEnabled } from "../../config/features";
import "./SiteVisualsPage.css";

const PointCloudViewer = lazy(() => import("./PointCloudViewer"));

const LAYER_KEYS = ["orthomosaic", "boundary", "grid", "annotations"];
const LAYER_FEATURES = {
  boundary: "SV_LAYER_BOUNDARY",
  grid: "SV_LAYER_GRID",
  annotations: "SV_LAYER_ANNOTATIONS",
};
const layerAvailable = (key) => !LAYER_FEATURES[key] || isFeatureEnabled(LAYER_FEATURES[key]);

const TABS = ["walkthrough", "orthomosaic", "model3d"];
const MAX_PIXEL_SCALE = 2;

function isGlb(path = "") {
  return /\.glb($|\?)/i.test(path);
}

function isPotree(path = "") {
  return /metadata\.json($|\?)/i.test(path) || /_potree\//i.test(path);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function computeFit(viewerW, viewerH, imageW, imageH) {
  if (!viewerW || !viewerH || !imageW || !imageH) return null;
  const nextScale = Math.min(viewerW / imageW, viewerH / imageH);
  return {
    scale: nextScale,
    x: (viewerW - imageW * nextScale) / 2,
    y: (viewerH - imageH * nextScale) / 2,
  };
}

function viewTransform(offset, scale) {
  return `translate(${offset.x}px, ${offset.y}px) scale(${scale})`;
}

function SiteVisualsPage() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { activeProject, activeSurvey, surveyDateLabel } = useModule();
  const [activeTab, setActiveTab] = useState("orthomosaic");
  const [compareMode, setCompareMode] = useState(false);
  const [split, setSplit] = useState(50);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageSize, setImageSize] = useState({ w: 0, h: 0 });
  const [viewerSize, setViewerSize] = useState({ w: 0, h: 0 });
  const [miniSrc, setMiniSrc] = useState("");
  const [viewReady, setViewReady] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [drawnTileSource, setDrawnTileSource] = useState(null);
  const [layers, setLayers] = useState({
    orthomosaic: true,
    boundary: false,
    grid: false,
    annotations: false,
  });
  const layerOn = (key) => layers[key] && layerAvailable(key);
  const [brokenMedia, setBrokenMedia] = useState({});
  const [mapImages, setMapImages] = useState([]);
  const [selectedImageId, setSelectedImageId] = useState(null);

  const viewerRef = useRef(null);
  const walkStageRef = useRef(null);
  const orthoImgRef = useRef(null);
  const stageRef = useRef(null);
  const compareStageRef = useRef(null);
  const miniBoxRef = useRef(null);
  const scaleLabelRef = useRef(null);
  const dragRef = useRef(null);
  const splitDragRef = useRef(false);
  const hasFittedRef = useRef(false);
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const imageSizeRef = useRef({ w: 0, h: 0 });
  const viewerSizeRef = useRef({ w: 0, h: 0 });
  const rafRef = useRef(0);
  const miniPendingRef = useRef(false);
  const osdRef = useRef(null);

  const projectCode = activeProject?.code || activeProject?.id || "";
  const projectKey = activeProject?.numericId || projectCode;
  const project = useMemo(
    () => ({
      id: projectCode,
      code: projectCode,
      name: activeProject?.name || "Project",
      location: activeProject?.location || "",
      lastSurvey: surveyDateLabel || "",
      lastSurveyAt: surveyDateLabel || "",
    }),
    [activeProject, surveyDateLabel, projectCode]
  );

  const fallbackMedia = useMemo(() => resolveProjectMedia(projectCode), [projectCode]);
  const survey = activeSurvey;

  // Load 2D / 3D / WALKTHROUGH site images for the selected survey
  useEffect(() => {
    let cancelled = false;
    const imageType =
      activeTab === "model3d"
        ? "3D"
        : activeTab === "orthomosaic"
          ? "2D"
          : activeTab === "walkthrough"
            ? "WALKTHROUGH"
            : null;
    if (!projectKey || !survey?.id || !imageType) {
      setMapImages([]);
      setSelectedImageId(null);
      setMediaLoading(false);
      return undefined;
    }

    setMediaLoading(true);
    fetchSurveyImages(projectKey, survey.id, imageType).then((rows) => {
      if (cancelled) return;
      setMediaLoading(false);
      setMapImages(rows);
      setSelectedImageId(rows[0]?.id ?? null);
      setBrokenMedia({});
      hasFittedRef.current = false;
      setViewReady(false);
      osdRef.current = null;
    });

    return () => {
      cancelled = true;
    };
  }, [projectKey, survey?.id, activeTab]);

  const selectedImage = mapImages.find((img) => img.id === selectedImageId) || mapImages[0];

  const media = useMemo(() => {
    // While site_images are loading, do not fall back to conventional/hardcoded
    // viewer URLs (avoids a flash request for the wrong .dzi / HLS / mesh).
    if (!selectedImage) {
      return {
        ...fallbackMedia,
        walkthrough: null,
        walkthroughOriginal: null,
        walkthroughMoments: null,
        orthomosaic: null,
        orthomosaicOriginal: null,
        orthomosaicTiles: null,
        modelMesh: null,
        pointCloud: null,
        pointCloudTiles: null,
      };
    }
    if (activeTab === "walkthrough") {
      // site_images WALKTHROUGH: file_path=.mp4, tile_path=HLS, preview_path=moments.json
      return {
        ...fallbackMedia,
        walkthrough: selectedImage.tileUrl || selectedImage.url || null,
        walkthroughOriginal: selectedImage.url || null,
        walkthroughMoments: selectedImage.previewUrl || null,
      };
    }
    if (activeTab === "model3d") {
      const path = selectedImage?.url || selectedImage?.filePath || "";
      return {
        ...fallbackMedia,
        folder: projectCode,
        modelMesh: isGlb(path) ? selectedImage.url : null,
        pointCloudTiles: isPotree(path) ? selectedImage.url : null,
        pointCloud: !isGlb(path) && !isPotree(path) ? selectedImage?.url : null,
      };
    }
    // orthomosaic / 2D — tile_path from site_images only
    return {
      ...fallbackMedia,
      folder: projectCode,
      orthomosaic: selectedImage.url || null,
      orthomosaicOriginal: selectedImage.url || null,
      orthomosaicTiles: selectedImage.tileUrl || null,
    };
  }, [selectedImage, activeTab, fallbackMedia, projectCode]);

  useEffect(() => {
    setCompareMode(false);
    scaleRef.current = 1;
    offsetRef.current = { x: 0, y: 0 };
    setImageSize({ w: 0, h: 0 });
    imageSizeRef.current = { w: 0, h: 0 };
    hasFittedRef.current = false;
    miniPendingRef.current = false;
    setMiniSrc("");
    setViewReady(false);
    setBrokenMedia({});
    osdRef.current = null;
  }, [projectCode, survey?.id, selectedImageId]);

  const compareSurvey = null;
  const useTiles = Boolean(media.orthomosaicTiles) && !brokenMedia.orthomosaicTiles;
  const markBroken = (key) => {
    setBrokenMedia((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };
  const stackLayout = activeTab === "orthomosaic" || activeTab === "model3d";
  const orthoSource = useTiles || Boolean(media.orthomosaic && !brokenMedia.orthomosaic);
  const orthoLoading =
    Boolean(survey) &&
    layers.orthomosaic &&
    (mediaLoading || (orthoSource && !(useTiles ? drawnTileSource === media.orthomosaicTiles : viewReady)));
  const orthoFailed =
    Boolean(survey) &&
    (brokenMedia.orthomosaic || (brokenMedia.orthomosaicTiles && !media.orthomosaic));
  const orthoUnavailable = Boolean(survey) && !mediaLoading && !orthoSource && !orthoFailed;

  const syncImageSize = (img) => {
    if (!img?.naturalWidth || !img.naturalHeight) return;
    const next = { w: img.naturalWidth, h: img.naturalHeight };
    imageSizeRef.current = next;
    setImageSize((prev) => {
      if (prev.w === next.w && prev.h === next.h) return prev;
      return next;
    });
    if (!miniPendingRef.current) {
      miniPendingRef.current = true;
      const width = 296;
      const height = Math.max(1, Math.round((width * next.h) / next.w));
      const paintMini = (source) => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(source, 0, 0, width, height);
        setMiniSrc(canvas.toDataURL("image/jpeg", 0.72));
      };
      if (typeof createImageBitmap === "function") {
        createImageBitmap(img, { resizeWidth: width, resizeHeight: height, resizeQuality: "low" })
          .then((bitmap) => {
            paintMini(bitmap);
            bitmap.close?.();
          })
          .catch(() => paintMini(img));
      } else {
        paintMini(img);
      }
    }
  };

  const paintView = useCallback(() => {
    const transform = viewTransform(offsetRef.current, scaleRef.current);
    if (stageRef.current) stageRef.current.style.transform = transform;
    if (compareStageRef.current) compareStageRef.current.style.transform = transform;

    const img = imageSizeRef.current;
    const viewer = viewerSizeRef.current;
    const scale = scaleRef.current;
    const offset = offsetRef.current;
    const fit = img.w && viewer.w && viewer.h
      ? Math.min(viewer.w / img.w, viewer.h / img.h)
      : 0;
    if (scaleLabelRef.current && fit) {
      scaleLabelRef.current.textContent = `${Math.max(1, Math.round(75 * clamp(fit / scale, 0.04, 1)))} m`;
    }
    if (miniBoxRef.current && img.w && scale) {
      const scaledW = img.w * scale;
      const scaledH = img.h * scale;
      const box = miniBoxRef.current;
      box.style.width = `${clamp((viewer.w / scaledW) * 100, 4, 100)}%`;
      box.style.height = `${clamp((viewer.h / scaledH) * 100, 4, 100)}%`;
      box.style.left = `${clamp((-offset.x / scaledW) * 100, 0, 96)}%`;
      box.style.top = `${clamp((-offset.y / scaledH) * 100, 0, 96)}%`;
    }
  }, []);

  const schedulePaint = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      paintView();
    });
  }, [paintView]);

  const fitLimits = useCallback(() => {
    const img = imageSizeRef.current;
    const viewer = viewerSizeRef.current;
    const fitScale = img.w && img.h && viewer.w && viewer.h
      ? Math.min(viewer.w / img.w, viewer.h / img.h)
      : 0;
    return {
      minScale: fitScale || 0.01,
      maxScale: Math.max(MAX_PIXEL_SCALE, fitScale),
    };
  }, []);

  const applyFit = useCallback(() => {
    if (osdRef.current) {
      fitTiledViewer(osdRef.current);
      setViewReady(true);
      return true;
    }
    const viewer = viewerRef.current;
    const img = orthoImgRef.current;
    const imageW = imageSizeRef.current.w || img?.naturalWidth || 0;
    const imageH = imageSizeRef.current.h || img?.naturalHeight || 0;
    if (!viewer || !imageW || !imageH) return false;
    const rect = viewer.getBoundingClientRect();
    const fitted = computeFit(rect.width, rect.height, imageW, imageH);
    if (!fitted) return false;
    scaleRef.current = fitted.scale;
    offsetRef.current = { x: fitted.x, y: fitted.y };
    paintView();
    setViewReady(true);
    return true;
  }, [paintView]);

  useEffect(() => {
    imageSizeRef.current = imageSize;
    viewerSizeRef.current = viewerSize;
    if (!imageSize.w || !viewerSize.w || !viewerSize.h || hasFittedRef.current) return;
    const fitted = computeFit(viewerSize.w, viewerSize.h, imageSize.w, imageSize.h);
    if (!fitted) return;
    hasFittedRef.current = true;
    scaleRef.current = fitted.scale;
    offsetRef.current = { x: fitted.x, y: fitted.y };
    paintView();
    setViewReady(true);
  }, [imageSize, viewerSize, paintView]);

  const resetView = () => {
    if (osdRef.current) {
      rotateTiledViewerHome(osdRef.current);
      return;
    }
    applyFit();
  };

  const zoomAt = useCallback((factor, pivotX, pivotY) => {
    if (osdRef.current) {
      zoomTiledViewer(osdRef.current, factor);
      return;
    }
    const { minScale, maxScale } = fitLimits();
    const prevScale = scaleRef.current;
    const next = clamp(prevScale * factor, minScale, maxScale);
    const ratio = next / prevScale;
    scaleRef.current = next;
    offsetRef.current = {
      x: pivotX - (pivotX - offsetRef.current.x) * ratio,
      y: pivotY - (pivotY - offsetRef.current.y) * ratio,
    };
    schedulePaint();
  }, [fitLimits, schedulePaint]);

  const zoomBy = useCallback((factor) => {
    if (osdRef.current) {
      zoomTiledViewer(osdRef.current, factor);
      return;
    }
    const viewer = viewerRef.current;
    if (!viewer) return;
    const rect = viewer.getBoundingClientRect();
    zoomAt(factor, rect.width / 2, rect.height / 2);
  }, [zoomAt]);

  const onPointerDown = (event) => {
    if (useTiles || splitDragRef.current) return;
    dragRef.current = {
      x: event.clientX - offsetRef.current.x,
      y: event.clientY - offsetRef.current.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    offsetRef.current = {
      x: event.clientX - dragRef.current.x,
      y: event.clientY - dragRef.current.y,
    };
    schedulePaint();
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  useEffect(() => {
    const node = viewerRef.current;
    if (!node || activeTab !== "orthomosaic" || useTiles) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      zoomAt(
        event.deltaY > 0 ? 1 / 1.12 : 1.12,
        event.clientX - rect.left,
        event.clientY - rect.top
      );
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [activeTab, zoomAt, useTiles]);

  const toggleFullscreen = async () => {
    const node = activeTab === "walkthrough" ? walkStageRef.current : viewerRef.current;
    if (!node) return;
    if (!document.fullscreenElement) {
      await node.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const downloadSurvey = () => {
    const href =
      activeTab === "walkthrough"
        ? media.walkthroughOriginal || media.walkthrough
        : activeTab === "model3d"
          ? media.modelMesh || media.pointCloud
          : media.orthomosaicOriginal || media.orthomosaic;
    if (!href) return;
    const link = document.createElement("a");
    link.href = href;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download =
      activeTab === "walkthrough"
        ? `${project.id}-walkthrough.mp4`
        : activeTab === "model3d"
          ? media.modelMesh
            ? `${project.id}-textured-model.glb`
            : `${project.id}-pointcloud.ply`
          : `${project.id}-orthomosaic.jpg`;
    link.click();
  };

  const onSplitMove = useCallback((event) => {
    if (!splitDragRef.current || !viewerRef.current) return;
    const rect = viewerRef.current.getBoundingClientRect();
    const next = ((event.clientX - rect.left) / rect.width) * 100;
    setSplit(clamp(next, 8, 92));
  }, []);

  useEffect(() => {
    const stop = () => {
      splitDragRef.current = false;
    };
    window.addEventListener("pointermove", onSplitMove);
    window.addEventListener("pointerup", stop);
    return () => {
      window.removeEventListener("pointermove", onSplitMove);
      window.removeEventListener("pointerup", stop);
    };
  }, [onSplitMove]);

  useEffect(() => {
    const node = viewerRef.current;
    if (!node) return undefined;
    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      const next = { w: rect.width, h: rect.height };
      viewerSizeRef.current = next;
      setViewerSize((prev) => (prev.w === next.w && prev.h === next.h ? prev : next));
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, [activeTab]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  useLayoutEffect(() => {
    paintView();
  }, [imageSize, miniSrc, paintView]);

  useEffect(() => {
    syncImageSize(orthoImgRef.current);
  }, [media.orthomosaic, selectedImageId]);

  return (
    <ConstructionLayout title="Site Visuals">
      <div className={`site-visuals-page ${isDarkMode ? "dark" : "light"} sv-embedded`}>
        <div className="sv-container">
          <div className="sv-tabs" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                className={`sv-tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {t(`siteVisuals.tabs.${tab}`)}
              </button>
            ))}
          </div>

          <div className={`sv-workspace ${stackLayout ? "sv-workspace-stack" : ""}`}>
            <section className="sv-viewer-col">
              {(activeTab === "orthomosaic" ||
                activeTab === "walkthrough" ||
                activeTab === "model3d") && (
                  <div className="sv-action-bar">
                    <div className="sv-date-wrap">
                      <span className="sv-action-btn sv-action-label">
                        <CalendarDays size={15} />
                        <span>
                          {survey
                            ? `${survey.dateLabel || surveyDateLabel}${survey.latest ? ` (${t("siteVisuals.latest")})` : ""}`
                            : t("siteVisuals.noSurvey")}
                        </span>
                      </span>
                    </div>

                    <Feature name="SV_COMPARE">
                      <button
                        type="button"
                        className={`sv-action-btn ${compareMode ? "active" : ""}`}
                        disabled={activeTab !== "orthomosaic" || !compareSurvey}
                        onClick={() => setCompareMode((value) => !value)}
                      >
                        <Columns2 size={15} />
                        <span>{t("siteVisuals.compare")}</span>
                      </button>
                    </Feature>

                    <div className="sv-action-spacer" />

                    <Feature name="SV_DOWNLOAD">
                      <button
                        type="button"
                        className="sv-action-btn"
                        onClick={downloadSurvey}
                        disabled={
                          !survey ||
                          (activeTab === "walkthrough" && !media.walkthroughOriginal) ||
                          (activeTab === "model3d" && !media.modelMesh && !media.pointCloud && !media.pointCloudTiles)
                        }
                      >
                        <Download size={15} />
                        <span>{t("siteVisuals.download")}</span>
                      </button>
                    </Feature>
                    <button
                      type="button"
                      className="sv-icon-btn"
                      onClick={toggleFullscreen}
                      aria-label={t("siteVisuals.fullscreen")}
                    >
                      {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    </button>
                  </div>
              )}

              {activeTab === "orthomosaic" && (
                  <div
                    className="sv-viewer"
                    ref={viewerRef}
                    onPointerDown={useTiles ? undefined : onPointerDown}
                    onPointerMove={useTiles ? undefined : onPointerMove}
                    onPointerUp={useTiles ? undefined : onPointerUp}
                    onPointerLeave={useTiles ? undefined : onPointerUp}
                  >
                    {survey ? (
                      <>
                        {useTiles ? (
                          <TiledOrthomosaicViewer
                            tileSource={media.orthomosaicTiles}
                            visible={layers.orthomosaic}
                            onReady={(viewer) => {
                              osdRef.current = viewer;
                              setViewReady(true);
                            }}
                            onError={() => markBroken("orthomosaicTiles")}
                            onLoadStart={() => setDrawnTileSource(null)}
                            onFirstDraw={() => setDrawnTileSource(media.orthomosaicTiles)}
                          />
                        ) : (
                          <div
                            className="sv-stage"
                            ref={stageRef}
                            style={{
                              width: imageSize.w || undefined,
                              height: imageSize.h || undefined,
                              visibility: viewReady ? "visible" : "hidden",
                            }}
                          >
                            {layers.orthomosaic && media.orthomosaic && !brokenMedia.orthomosaic && (
                              <img
                                ref={orthoImgRef}
                                key={media.orthomosaic}
                                src={media.orthomosaic}
                                alt={t("siteVisuals.orthomosaicAlt")}
                                draggable={false}
                                decoding="async"
                                className="sv-ortho-image"
                                onError={() => markBroken("orthomosaic")}
                                onLoad={(event) => syncImageSize(event.currentTarget)}
                              />
                            )}
                            {layerOn("grid") && <div className="sv-grid-overlay" />}
                            {layerOn("boundary") && (
                              <svg className="sv-boundary" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <polygon
                                  points="12,18 88,14 92,86 8,82"
                                  fill="rgba(0, 130, 248, 0.08)"
                                  stroke="#3b9eff"
                                  strokeWidth="0.6"
                                  strokeDasharray="1.6 1.2"
                                />
                              </svg>
                            )}
                            {layerOn("annotations") && (
                              <div className="sv-annotations">
                                <span className="sv-pin" style={{ left: "32%", top: "38%" }}>A</span>
                                <span className="sv-pin" style={{ left: "61%", top: "46%" }}>B</span>
                                <span className="sv-pin" style={{ left: "48%", top: "68%" }}>C</span>
                              </div>
                            )}
                          </div>
                        )}

                        {useTiles && (
                          <>
                            {layerOn("grid") && <div className="sv-grid-overlay" />}
                            {layerOn("boundary") && (
                              <svg className="sv-boundary" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <polygon
                                  points="12,18 88,14 92,86 8,82"
                                  fill="rgba(0, 130, 248, 0.08)"
                                  stroke="#3b9eff"
                                  strokeWidth="0.6"
                                  strokeDasharray="1.6 1.2"
                                />
                              </svg>
                            )}
                            {layerOn("annotations") && (
                              <div className="sv-annotations">
                                <span className="sv-pin" style={{ left: "32%", top: "38%" }}>A</span>
                                <span className="sv-pin" style={{ left: "61%", top: "46%" }}>B</span>
                                <span className="sv-pin" style={{ left: "48%", top: "68%" }}>C</span>
                              </div>
                            )}
                          </>
                        )}

                        {compareMode && compareSurvey && (
                          <div
                            className="sv-compare-clip"
                            style={{ clipPath: `inset(0 0 0 ${split}%)` }}
                          >
                            <div
                              className="sv-stage"
                              ref={compareStageRef}
                              style={{
                                width: imageSize.w || undefined,
                                height: imageSize.h || undefined,
                              }}
                            >
                              <img
                                src={compareSurvey.orthomosaic}
                                alt={t("siteVisuals.compareAlt")}
                                draggable={false}
                                className="sv-ortho-image"
                                onError={() => markBroken("orthomosaic")}
                              />
                            </div>
                          </div>
                        )}

                        {compareMode && compareSurvey && (
                          <div
                            className="sv-split-handle"
                            style={{ left: `${split}%` }}
                            onPointerDown={(event) => {
                              event.stopPropagation();
                              splitDragRef.current = true;
                            }}
                          >
                            <span />
                          </div>
                        )}

                        {orthoLoading && (
                          <ViewerLoading
                            label={t(mediaLoading ? "siteVisuals.loadingMedia" : "siteVisuals.loadingOrthomosaic")}
                          />
                        )}
                        {(orthoUnavailable || orthoFailed) && (
                          <div className="sv-empty sv-empty-overlay">
                            <p>
                              {t(
                                orthoFailed
                                  ? "siteVisuals.orthomosaicFailed"
                                  : "siteVisuals.orthomosaicUnavailable"
                              )}
                            </p>
                          </div>
                        )}

                        <div
                          className="sv-layers-panel"
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          {LAYER_KEYS.filter(layerAvailable).map((key) => (
                            <label key={key} className="sv-layer-row">
                              <input
                                type="checkbox"
                                checked={layers[key]}
                                onChange={() =>
                                  setLayers((prev) => ({ ...prev, [key]: !prev[key] }))
                                }
                              />
                              <span>{t(`siteVisuals.layers.${key}`)}</span>
                            </label>
                          ))}
                        </div>

                        <div
                          className="sv-nav-controls"
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          <button type="button" onClick={resetView} title={t("siteVisuals.resetView")}>
                            <Compass size={16} />
                          </button>
                          <button type="button" onClick={() => zoomBy(1.25)} title={t("siteVisuals.zoomIn")}>
                            <Plus size={16} />
                          </button>
                          <button type="button" onClick={() => zoomBy(1 / 1.25)} title={t("siteVisuals.zoomOut")}>
                            <Minus size={16} />
                          </button>
                          <button type="button" onClick={toggleFullscreen} title={t("siteVisuals.fullscreen")}>
                            <Maximize2 size={15} />
                          </button>
                          <Feature name="SV_LOCATE_BUTTON">
                            <button type="button" onClick={resetView} title={t("siteVisuals.locate")}>
                              <Crosshair size={16} />
                            </button>
                          </Feature>
                        </div>

                        <Feature name="SV_SCALE_BAR">
                          <div className="sv-scale">
                            <span>0</span>
                            <div className="sv-scale-bar" />
                            <span ref={scaleLabelRef}>75 m</span>
                          </div>
                        </Feature>

                        {!useTiles && (
                        <div
                          className="sv-minimap"
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          {miniSrc ? (
                            <img src={miniSrc} alt="" draggable={false} />
                          ) : null}
                          <div className="sv-minimap-box" ref={miniBoxRef} />
                        </div>
                        )}
                      </>
                    ) : (
                      <div className="sv-empty">
                        <p>{t("siteVisuals.noSurveyBody")}</p>
                      </div>
                    )}
                  </div>
              )}

              {activeTab === "walkthrough" && (
                mediaLoading ? (
                  <div className="sv-viewer">
                    <ViewerLoading label={t("siteVisuals.loadingMedia")} />
                  </div>
                ) : media.walkthrough && !brokenMedia.walkthrough ? (
                  <WalkthroughPlayer
                    key={media.walkthrough}
                    streamUrl={media.walkthrough}
                    momentsUrl={media.walkthroughMoments}
                    label={t("siteVisuals.tabs.walkthrough")}
                    onFullscreen={toggleFullscreen}
                    isFullscreen={isFullscreen}
                    stageRef={walkStageRef}
                  />
                ) : (
                  <div className="sv-placeholder">
                    <PlayCircle size={36} />
                    <h3>{t("siteVisuals.walkthroughTitle")}</h3>
                    <p>
                      {t("siteVisuals.missingMedia", {
                        folder: media.folder || project.code || project.id,
                        file: "site_images WALKTHROUGH (tile_path HLS)",
                      })}
                    </p>
                  </div>
                )
              )}

              {activeTab === "model3d" && (
                mediaLoading ? (
                  <div className="sv-viewer sv-viewer-3d">
                    <ViewerLoading label={t("siteVisuals.loadingMedia")} />
                  </div>
                ) : (media.modelMesh && !brokenMedia.modelMesh) ||
                (media.pointCloudTiles && !brokenMedia.pointCloudTiles) ? (
                  <div className="sv-viewer sv-viewer-3d" ref={viewerRef}>
                    <Suspense fallback={<ViewerLoading label={t("siteVisuals.modelLoading")} />}>
                      <PointCloudViewer
                        key={`${media.modelMesh || ""}|${media.pointCloudTiles || ""}`}
                        metadataUrl={
                          media.pointCloudTiles && !brokenMedia.pointCloudTiles
                            ? media.pointCloudTiles
                            : null
                        }
                        meshUrl={
                          media.modelMesh && !brokenMedia.modelMesh
                            ? media.modelMesh
                            : null
                        }
                        onError={() => {
                          if (media.modelMesh) markBroken("modelMesh");
                          if (media.pointCloudTiles) markBroken("pointCloudTiles");
                        }}
                      />
                    </Suspense>
                  </div>
                ) : (
                  <div className="sv-placeholder">
                    <Box size={36} />
                    <h3>{t("siteVisuals.modelTitle")}</h3>
                    <p>
                      {t("siteVisuals.missingMedia", {
                        folder: media.folder || project.code || project.id,
                        file: "pointcloud_potree/metadata.json",
                      })}
                    </p>
                  </div>
                )
              )}
            </section>

            {stackLayout ? (
              <div className="sv-bottom-panel">
                <div className="sv-details-card">
                  <h3>{t("siteVisuals.surveyDetails")}</h3>
                  {survey ? (
                    <dl>
                      <div>
                        <dt>{t("siteVisuals.fields.surveyDate")}</dt>
                        <dd>{survey.dateLabel || surveyDateLabel}</dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.drone")}</dt>
                        <dd>{survey.drone || "—"}</dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.capturedImages")}</dt>
                        <dd>{survey.capturedImages || "—"}</dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.groundResolution")}</dt>
                        <dd>{survey.groundResolution || "—"}</dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.areaCovered")}</dt>
                        <dd>{survey.areaCovered || "—"}</dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.weather")}</dt>
                        <dd className="sv-weather">
                          <Feature name="SV_WEATHER_ICON"><Sun size={14} /></Feature>
                          {survey.weather || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.flightDuration")}</dt>
                        <dd>{survey.flightDuration || "—"}</dd>
                      </div>
                    </dl>
                  ) : (
                    <p className="sv-details-empty">{t("siteVisuals.noSurveyBody")}</p>
                  )}
                </div>

                <div className="sv-map-modes-card">
                  <h3>{t("siteVisuals.mapModes", { defaultValue: "Map Modes" })}</h3>
                  {mapImages.length === 0 ? (
                    <p className="sv-details-empty">
                      {t("siteVisuals.noMapModes", {
                        defaultValue: "No images for this survey yet.",
                      })}
                    </p>
                  ) : (
                    <div className="sv-map-modes-grid">
                      {mapImages.map((img) => (
                        <button
                          key={img.id}
                          type="button"
                          className={`sv-map-mode ${selectedImage?.id === img.id ? "active" : ""}`}
                          onClick={() => setSelectedImageId(img.id)}
                        >
                          <div className="sv-map-mode-thumb">
                            {img.previewUrl || img.url ? (
                              <img src={img.previewUrl || img.url} alt="" />
                            ) : (
                              <Box size={28} />
                            )}
                          </div>
                          <span className="sv-map-mode-label">{img.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <aside className="sv-side">
                <div className="sv-details-card">
                  <h3>{t("siteVisuals.surveyDetails")}</h3>
                  {survey ? (
                    <dl>
                      <div>
                        <dt>{t("siteVisuals.fields.surveyDate")}</dt>
                        <dd>{survey.dateLabel || surveyDateLabel}</dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.drone")}</dt>
                        <dd>{survey.drone || "—"}</dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.capturedImages")}</dt>
                        <dd>{survey.capturedImages || "—"}</dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.groundResolution")}</dt>
                        <dd>{survey.groundResolution || "—"}</dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.areaCovered")}</dt>
                        <dd>{survey.areaCovered || "—"}</dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.weather")}</dt>
                        <dd className="sv-weather">
                          <Feature name="SV_WEATHER_ICON"><Sun size={14} /></Feature>
                          {survey.weather || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt>{t("siteVisuals.fields.flightDuration")}</dt>
                        <dd>{survey.flightDuration || "—"}</dd>
                      </div>
                    </dl>
                  ) : (
                    <p className="sv-details-empty">{t("siteVisuals.noSurveyBody")}</p>
                  )}
                </div>
              </aside>
            )}
          </div>
        </div>
      </div>
    </ConstructionLayout>
  );
}

export default SiteVisualsPage;
